import { Button } from "@ondit/ui";
import { X } from "lucide-react";
import { Link } from "react-router";
import { useAccess, whyDisabled } from "~/core/access/index";
import type { NextAction } from "~/core/api/index";
import { useStored } from "~/core/hooks";
import { useT } from "~/core/i18n";
import { Hint } from "~/core/ui/page";
import { StageIcon } from "~/modules/analytics/components/insight-icon";
import { actionPath } from "~/modules/analytics/lib/action-path";
import { LOOP } from "~/modules/analytics/lib/loop";

/**
 * What to do next, computed rather than authored.
 *
 * A panel of eleven numbers and no next step is a panel someone opens twice.
 * Every card here comes from this brand's own data — five open gaps, three
 * drafts waiting, a balance that cannot cover the next page — and says the
 * consequence rather than the instruction, because a customer who knows why a
 * thing matters decides for themselves whether to do it.
 *
 * The order comes from the server: blocked before merely possible, then in the
 * order of the loop. Sorting by size would put nine small things above one
 * thing that has stopped the whole thing working.
 */
export function TodoList({
	entityId,
	actions,
	onSample,
	sampling,
	compact = false,
}: {
	entityId: string;
	actions: NextAction[];
	/** The one card whose action is not a screen: it starts a run. */
	onSample: () => void;
	sampling: boolean;
	compact?: boolean;
}) {
	const t = useT();
	// Per browser, and never a permanent silence: a hidden card comes back as a
	// count with one click to restore it. A dismissal that cannot be undone
	// turns "not useful this week" into "never tell me again", which is a
	// different decision than the one the person made.
	const [hiddenRaw, setHidden] = useStored<string>("ondit.todoHidden", "");
	const hidden = new Set(hiddenRaw.split(",").filter(Boolean));
	const visible = actions.filter((action) => !hidden.has(action.code));
	const buried = actions.length - visible.length;

	if (actions.length === 0) {
		return (
			<div className="panel todo-empty">
				<p className="pane-label">{t("todo.empty")}</p>
				<p className="faint">{t("todo.emptyBody")}</p>
			</div>
		);
	}

	return (
		<div className={compact ? "todo todo-compact" : "todo"}>
			{visible.map((action) => (
				<Card
					key={action.code}
					entityId={entityId}
					action={action}
					compact={compact}
					onSample={onSample}
					sampling={sampling}
					onHide={() => setHidden([...hidden, action.code].join(","))}
				/>
			))}
			{buried > 0 ? (
				<p className="faint">
					{t("todo.hidden", { n: buried })}{" "}
					<Button
						variant="link"
						className="link-button h-auto justify-start whitespace-normal p-0 text-left"
						type="button"
						onClick={() => setHidden("")}
					>
						{t("todo.showHidden")}
					</Button>
				</p>
			) : null}
		</div>
	);
}

function Card({
	entityId,
	action,
	onSample,
	sampling,
	compact = false,
	onHide,
}: {
	entityId: string;
	action: NextAction;
	onSample: () => void;
	sampling: boolean;
	compact?: boolean;
	onHide: () => void;
}) {
	const t = useT();
	const { editor, why } = useAccess();
	const n = String(action.count);
	const attention = action.severity === "attention";
	const label = t(`todo.${action.code}.cta`);
	// Costs, validation and blocked-action guidance remain visible in a compact list.
	const showBody =
		attention ||
		["publishing_review", "pages_review", "few_datapoints", "low_credits", "never_sampled", "sampling_now"].includes(
			action.code,
		);
	const body = t(`todo.${action.code}.body`, { n });

	return (
		<article className={attention ? "todo-card todo-attention" : "todo-card"}>
			{compact ? <StageIcon step={action.stage} /> : null}
			<div className="todo-text">
				<div className="todo-line">
					{!compact || attention ? (
						<span className={attention ? "pill pill-warning" : "pill pill-accent"}>
							{t(attention ? "todo.attention" : "todo.opportunity")}
						</span>
					) : null}
					<h3>{t(`todo.${action.code}.title`, { n })}</h3>
					{compact && !showBody ? <Hint text={body} /> : null}
					<span className="faint">
						{t("nav.stage.n", { n: String(action.stage) })} ·{" "}
						{t(`nav.stage.${LOOP[action.stage - 1]?.key ?? "detect"}`)}
					</span>
				</div>
				{!compact ? <p>{body}</p> : null}
			</div>
			{compact && showBody ? <p className="todo-guidance">{body}</p> : null}

			<div className="todo-act">
				{action.code === "never_sampled" ? (
					<Button
						variant="default"
						type="button"
						onClick={onSample}
						disabled={sampling || !editor}
						title={whyDisabled(editor, why.editor)}
					>
						{label}
					</Button>
				) : (
					<Button asChild variant="default">
						<Link to={actionPath(entityId, action.code)}>{label}</Link>
					</Button>
				)}
				{/* Last, past the action: dismissing is never the thing next to the
				    thing it would undo. */}
				<Button
					variant="ghost"
					size="icon"
					className="todo-hide"
					type="button"
					onClick={onHide}
					aria-label={t("todo.hide")}
				>
					<X aria-hidden />
				</Button>
			</div>
		</article>
	);
}
